import { useEffect, useMemo, useState } from 'react'

import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import { getExcludedEmailsKey } from '../lib/getExcludedEmailsKey'
import { getTopPartnerId } from '../lib/getTopPartnerId'

const PAGE_CONFIG = { page: 0, pageSize: 10 }

export const usePartnerSignerPickerSource = (partnerId: string, excludedEmails: string[] = []) => {
  const [topPartnerId, setTopPartnerId] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isActive = true

    setTopPartnerId('')
    setIsLoading(true)

    getTopPartnerId(partnerId)
      .then((resolvedTopPartnerId) => {
        if (!isActive) {
          return
        }

        setTopPartnerId(resolvedTopPartnerId || partnerId)
      })
      .catch(() => {
        if (!isActive) {
          return
        }

        setTopPartnerId(partnerId)
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [partnerId])

  const excludedEmailsKey = getExcludedEmailsKey(excludedEmails)

  const source = useMemo(() => {
    if (!topPartnerId) {
      return null
    }

    const crud = CrudStore.get(
      'ContactAllData',
      `acknowledgment-partner-picker:${partnerId}:${excludedEmailsKey}`
    )

    crud.setDefaultFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'TopPartnerID',
          operator: Operator.EQUAL,
          value: topPartnerId,
        },
        ...(excludedEmailsKey ? excludedEmailsKey.split(';') : []).map((email) => ({
          field: 'Email',
          operator: Operator.NOTEQUAL,
          value: email,
        })),
      ],
    })

    crud.setFilter({
      logic: Logic.AND,
      filters:
        partnerId === topPartnerId
          ? []
          : [
              {
                field: 'PartnerID',
                operator: Operator.EQUAL,
                value: partnerId,
              },
            ],
    })

    crud.setSort([
      {
        field: 'IsKeyContact',
        dir: 'desc',
      },
      {
        field: 'LastName',
        dir: 'asc',
      },
    ])

    crud.setPaging({
      ...PAGE_CONFIG,
    })

    return crud
  }, [excludedEmailsKey, partnerId, topPartnerId])

  return {
    source,
    isLoading,
  }
}
